import React, { useLayoutEffect, useMemo, useRef, useState } from "react";

// Small avatar for the mention dropdown rows.
function MiniAvatar({ name, img, primary }) {
  if (img) {
    return <img src={img} alt="" className="h-6 w-6 shrink-0 rounded-full object-cover" />;
  }
  const letter = (name || "?").charAt(0).toUpperCase();
  return (
    <span
      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white"
      style={{ backgroundColor: primary }}
    >
      {letter}
    </span>
  );
}

/**
 * A textarea with @-mention autocomplete. Members are picked from `members`
 * (the group roster). On pick, "@Name " is inserted at the caret and the mention
 * (name + token) is reported via onAddMention so the parent can serialize on submit.
 *
 * `limit` caps how many members can be tagged (null = unlimited). When the cap is
 * reached the dropdown is blocked with a hint. `tagged` = current mention count.
 */
export default function MentionTextarea({
  value,
  onChange,
  onAddMention,
  members = [],
  limit = null,
  tagged = 0,
  primary = "#16a34a",
  placeholder = "",
  className = "",
  rows = 3,
  taRef,
  onKeyDown,
  autoFocus = false,
  wrapperClassName = "relative",
  autoGrow = false,
}) {
  const innerRef = useRef(null);
  const areaRef = taRef || innerRef;

  // Grow the textarea to fit its content; the CSS max-height on the field caps
  // it, after which it scrolls. Runs on every value change.
  useLayoutEffect(() => {
    if (!autoGrow) return;
    const el = areaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [value, autoGrow, areaRef]);
  const [query, setQuery] = useState(null); // active @query string, or null
  const [anchor, setAnchor] = useState(0); // index of the '@' that started the query
  const [active, setActive] = useState(0); // highlighted row

  const atLimit = limit != null && tagged >= limit;

  // Members matching the current @query (by name), excluding nothing — the parent
  // may still tag the same person twice; that's fine (one notification each dedupes).
  const matches = useMemo(() => {
    if (query == null) return [];
    const q = query.toLowerCase();
    return members
      .filter((m) => m.name && (q === "" || m.name.toLowerCase().includes(q)))
      .slice(0, 6);
  }, [query, members]);

  const open = query != null && matches.length > 0;

  // Detect an @query immediately before the caret: "@" then word chars (no spaces
  // beyond a single trailing word). We keep it simple: @ + up to 20 non-space chars.
  const syncQuery = (el) => {
    const pos = el.selectionStart ?? el.value.length;
    const upto = el.value.slice(0, pos);
    const m = upto.match(/(?:^|\s)@([^\s@]{0,20})$/);
    if (m) {
      setQuery(m[1]);
      setAnchor(pos - m[1].length - 1); // position of '@'
      setActive(0);
    } else {
      setQuery(null);
    }
  };

  const handleChange = (e) => {
    onChange(e.target.value);
    syncQuery(e.target);
  };

  const pick = (m) => {
    const el = areaRef.current;
    if (!el) return;
    const pos = el.selectionStart ?? value.length;
    const before = value.slice(0, anchor);
    const after = value.slice(pos);
    const insert = `@${m.name} `;
    const next = before + insert + after;
    onChange(next);
    onAddMention?.({ name: m.name, token: m.token });
    setQuery(null);
    // Restore caret just after the inserted mention.
    requestAnimationFrame(() => {
      const caret = (before + insert).length;
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  };

  const handleKeyDown = (e) => {
    if (open && !atLimit) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActive((a) => (a + 1) % matches.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActive((a) => (a - 1 + matches.length) % matches.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        pick(matches[active]);
        return;
      }
      if (e.key === "Escape") {
        setQuery(null);
        return;
      }
    }
    onKeyDown?.(e);
  };

  return (
    <div className={wrapperClassName}>
      <textarea
        ref={areaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onClick={(e) => syncQuery(e.target)}
        onBlur={() => setTimeout(() => setQuery(null), 150)}
        placeholder={placeholder}
        className={className}
        rows={rows}
        autoFocus={autoFocus}
      />
      {query != null && (
        <div className="absolute left-2 right-2 top-full z-[70] mt-1 max-h-56 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-xl">
          {atLimit ? (
            <p className="px-3 py-2.5 text-xs text-gray-500">
              You can tag up to {limit} member{limit === 1 ? "" : "s"}.
            </p>
          ) : matches.length === 0 ? (
            <p className="px-3 py-2.5 text-xs text-gray-400">No members found</p>
          ) : (
            matches.map((m, i) => (
              <button
                key={m.token || i}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(m);
                }}
                className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm ${
                  i === active ? "bg-gray-50" : "hover:bg-gray-50"
                }`}
              >
                <MiniAvatar name={m.name} img={m.img} primary={primary} />
                <span className="flex-1 truncate text-gray-800">{m.name}</span>
                {m.is_admin && (
                  <span className="text-[10px] font-bold" style={{ color: primary }}>
                    Admin
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
